import { ANALYTICS_PLATFORM } from 'dpl/constants/analytics';
import { UseResourceMutationOptions } from 'dpl/shared/fetching/hooks/useResourceMutation';
import { UseResourceQueryOptions } from 'dpl/shared/fetching/hooks/useResourceQuery';
import { QUERY_KEY_PREFIXES } from 'dpl/shared/fetching/utils/constants';

import {
  BreederPerformanceFactorResponse,
  BreederProgramFactorResponse,
  BreederStatsCountsResource,
  BreederStatsFaqsResponse,
  BreederStatsHelpInfoResponse,
  BreederStatsImproveHealthTestsResponse,
  BreederTopPerformingListingsResponse,
  BreederTrafficSourcesResponse,
  BreedHealthTiersResponse,
  LitterOptionsResponse,
  RestoreGuidanceResponse
} from '../constants/resource_types';

interface BuyerAssignedLitterMutationParams {
  id: number | string;
  litterId: number | string | null;
  dogId: number | string | null;
  paymentRequestId?: number | string;
  analytics?: Record<string, unknown>;
}

export function activeLittersV1QueryDefinition(): UseResourceQueryOptions {
  return {
    url: '/api/breeder_app/active_litters/v1.json',
    defaultValue: []
  };
}

export function breederLitterOptionsQueryDefinition(
  breederProfileId: number | string,
  params: Record<string, unknown> = {}
): UseResourceQueryOptions<LitterOptionsResponse> {
  return {
    url: `/api/options/breeder_profiles/${breederProfileId}/litters.json`,
    enabled: Boolean(breederProfileId),
    params,
    defaultValue: { data: [] }
  };
}

export function buyerAssignedLitterV1QueryDefinition({
  id
}: {
  id: number | string;
}): UseResourceQueryOptions {
  return {
    createFetchDataConfig: { ignoreRescueOn: [404] },
    enabled: Boolean(id),
    url: `/api/breeder_app/buyer_assigned_litter/v1/${id}.json`
  };
}

export function buyerPreferredLitterV1QueryDefinition({
  id
}: {
  id: number | string;
}): UseResourceQueryOptions {
  return {
    createFetchDataConfig: { ignoreRescueOn: [404] },
    enabled: Boolean(id),
    url: `/api/breeder_app/buyer_preferred_litter/v1/${id}.json`
  };
}

export function updateUserMutationDefinition(): UseResourceMutationOptions {
  return {
    method: 'PUT',
    url: '/api/user_profiles.json'
  };
}

export function buyerAssignedLitterV1MutationDefinition({
  id,
  litterId,
  dogId,
  paymentRequestId,
  analytics = {}
}: BuyerAssignedLitterMutationParams): UseResourceMutationOptions {
  return {
    method: 'PUT',
    url: `/api/breeder_app/buyer_assigned_litter/v1/${id}.json`,
    params: {
      litter_id: litterId,
      dog_id: dogId,
      payment_request_id: paymentRequestId,
      analytics: {
        ...analytics,
        origin_platform: ANALYTICS_PLATFORM
      }
    }
  };
}

// Breeder insights (Opportunities): Performance factors
export function breederStatsListingsCompletenessFactorQueryDefinition(): UseResourceQueryOptions<BreederPerformanceFactorResponse> {
  return {
    url: '/api/breeder_stats/listings_completeness_factor.json',
    defaultValue: { meta: {}, data: {} }
  };
}

export function breederStatsHealthTestsFactorQueryDefinition(): UseResourceQueryOptions<BreederPerformanceFactorResponse> {
  return {
    url: '/api/breeder_stats/health_tests_factor.json',
    defaultValue: { meta: {}, data: {} }
  };
}

export function breedHealthTiersQueryDefinition(
  breedId: number | string,
  sizeId: number | string | null
): UseResourceQueryOptions<BreedHealthTiersResponse> {
  return {
    url: `/api/breeds/${breedId}/breed_health_tiers.json`,
    params: {
      breeder_waitlist_preference_id: sizeId
    },
    defaultValue: { data: { breed_health_tiers: [] } },
    enabled: Boolean(breedId)
  };
}

export function breederStatsTestimonialsFactorQueryDefinition(): UseResourceQueryOptions<BreederPerformanceFactorResponse> {
  return {
    url: '/api/breeder_stats/testimonials_factor.json',
    defaultValue: { meta: {}, data: {} }
  };
}

export function breederStatsHowToImproveTestimonialsFactorQueryDefinition(): UseResourceQueryOptions {
  return {
    url: '/api/breeder_stats/how_to_improve/testimonials_factor.json',
    defaultValue: { meta: {}, data: { testimonial_requests: [] } }
  };
}

export function breederStatsPlacementRateFactorQueryDefinition(): UseResourceQueryOptions<BreederPerformanceFactorResponse> {
  return {
    url: '/api/breeder_stats/placement_rate_factor.json',
    defaultValue: { meta: {}, data: {} }
  };
}

// Breeder insights (Stats): Program stats
export function breederStatsEngagementStatQueryDefinition(): UseResourceQueryOptions<BreederProgramFactorResponse> {
  return {
    url: '/api/breeder_stats/engagement_factor.json',
    defaultValue: { meta: {}, data: { items: [] } }
  };
}

export function breederStatsResponsivenessStatQueryDefinition(): UseResourceQueryOptions<BreederProgramFactorResponse> {
  return {
    url: '/api/breeder_stats/responsiveness_factor.json',
    defaultValue: { meta: {}, data: { items: [] } }
  };
}

export function breederStatsDogPlacementsStatQueryDefinition(): UseResourceQueryOptions<BreederProgramFactorResponse> {
  return {
    url: '/api/breeder_stats/dog_placements_factor.json',
    defaultValue: { meta: {}, data: { items: [] } }
  };
}

// Breeder insights (Stats): Top Performing Listings
export function breederStatsTopPerformingListingsQueryDefinition(): UseResourceQueryOptions<BreederTopPerformingListingsResponse> {
  return {
    url: '/api/breeder_stats/top_performing_listings.json',
    defaultValue: { meta: {}, data: { items: [], title: null, caption: null } }
  };
}

// Breeder insights (Stats): Traffic sources
export function breederStatsTrafficSourcesQueryDefinition(): UseResourceQueryOptions<BreederTrafficSourcesResponse> {
  return {
    url: '/api/breeder_stats/traffic_sources.json',
    defaultValue: { meta: {}, data: { items: [], title: null } }
  };
}

// Breeder insights: Counts
export function breederStatsCountsQueryDefinition(): UseResourceQueryOptions<BreederStatsCountsResource> {
  return {
    url: '/api/breeder_stats/counts.json'
  };
}

// Breeder insights: FAQs
export function breederStatsFAQsQueryDefinition(): UseResourceQueryOptions<BreederStatsFaqsResponse> {
  return {
    url: '/api/breeder_stats/faqs.json',
    defaultValue: { meta: {}, data: [] }
  };
}

// Breeder insights: Help info
export function breederStatsHelpInfoQueryDefinition(): UseResourceQueryOptions<BreederStatsHelpInfoResponse> {
  return {
    url: '/api/breeder_stats/help_info.json',
    defaultValue: { meta: {}, data: {} }
  };
}

export function breederStatsImproveHealthTestsQueryDefinition(): UseResourceQueryOptions<BreederStatsImproveHealthTestsResponse> {
  return {
    url: '/api/breeder_stats/how_to_improve/health_testing_parents.json',
    defaultValue: {
      meta: {},
      data: { sires_count: 0, dams_count: 0, items: [] }
    }
  };
}

export function breederStatsImproveIncompleteListingsQueryDefinition(): UseResourceQueryOptions {
  return {
    url: '/api/breeder_stats/how_to_improve/incomplete_listing_dogs.json',
    defaultValue: { meta: {}, data: [] }
  };
}

export function onboardingChecklistQueryDefinition(
  breederProfileId: number | string
): UseResourceQueryOptions {
  return {
    url: `/api/breeder_app/breeder_profiles/${breederProfileId}/onboarding_checklist`,
    prefix: QUERY_KEY_PREFIXES.SCREENING_QUIZ,
    defaultValue: {
      data: {
        user: {}
      }
    }
  };
}

export function estimateTaxesMutationDefinition(
  breederProfileId: number | string
): UseResourceMutationOptions {
  return {
    method: 'POST',
    url: `/api/breeder_app/breeder_profiles/${breederProfileId}/tax_estimates`
  };
}

export function litterMutationDefinition(
  breederProfileId: number | string,
  litterId: number | string
): UseResourceMutationOptions {
  return {
    url: `/api/breeder_app/breeder_profiles/${breederProfileId}/litters/${litterId}.json`,
    method: 'PUT'
  };
}

export function breederPaymentEnforcementRestoreGuidanceQueryDefinition(
  breederProfileId: number | string,
  enabled: boolean = true
): UseResourceQueryOptions<RestoreGuidanceResponse> {
  return {
    url: `/api/breeder_app/breeder_profiles/${breederProfileId}/breeder_payment_enforcement/restore_guidance`,
    enabled: Boolean(breederProfileId) && enabled,
    defaultValue: null
  };
}

export function breederAppLitterApplicantsResourceDefinition(
  breederProfileId: number | string,
  litterId: number | string
): UseResourceQueryOptions {
  return {
    url: `/api/breeder_profiles/${breederProfileId}/litters/${litterId}/applicants.json`,
    prefix: QUERY_KEY_PREFIXES.BREEDER_APP_LITTER_APPLICANTS,
    defaultValue: {
      data: []
    },
    enabled: Boolean(breederProfileId) && Boolean(litterId)
  };
}
